import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { SectionHeading } from '../ui/SharedUI';

export function CoreDivisions() {
  return (
    <section className="relative z-10 w-full py-24 lg:py-40 px-6 border-t border-white/5 bg-[#030303]">
      <div className="max-w-7xl mx-auto">
        <SectionHeading title="Core Divisions" subtitle="The Empire" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16 auto-rows-min">
          {/* Box 1: Content & Persona Engine */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="md:col-span-2 lg:col-span-2 p-8 md:p-12 border border-white/5 bg-white/[0.01] hover:bg-white/[0.02] backdrop-blur-sm transition-colors relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand/5 blur-[100px] rounded-full mix-blend-screen group-hover:bg-brand/10 transition-colors" />
            <h3 className="font-serif text-3xl mb-4 text-white">Content & Persona Scale</h3>
            <p className="text-neutral-400 font-light text-sm md:text-base leading-relaxed max-w-lg">
              Operating at the bleeding edge of social engagement. Our automated pipelines generate hyper-targeted, high-retention content at a volume and quality impossible for traditional agencies. Fully autonomous storytelling, engineered to scale your digital presence effortlessly.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <span className="px-3 py-1 border border-white/10 text-[10px] uppercase tracking-widest text-white/50 rounded-full">Automated Publishing</span>
              <span className="px-3 py-1 border border-white/10 text-[10px] uppercase tracking-widest text-white/50 rounded-full">Sentiment Analysis</span>
            </div>
          </motion.div>

          {/* Box 2: Replyra Platform */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
            className="p-8 border border-white/5 bg-[linear-gradient(45deg,#000,#050505)] hover:border-brand/30 transition-colors group flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/replyra-logo.png" alt="Replyra AI" className="w-8 h-8 object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.1)]" />
                <h3 className="font-serif text-2xl text-white mb-0">Replyra</h3>
              </div>
              <p className="text-neutral-400 font-light text-sm leading-relaxed">
                The central SaaS platform for social media & messaging automation. Giving the everyday business owner an entire automated customer service team.
              </p>
            </div>
            <a href="https://replyra.me" target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-brand hover:text-white transition-colors w-max">
              Enter Platform <ChevronRight size={12} />
            </a>
          </motion.div>

          {/* Box 3: Replyra Forge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
            className="p-8 border border-white/5 bg-[linear-gradient(45deg,#000,#0a0a0a)] hover:border-brand/30 transition-colors group flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/replyra-logo.png" alt="Replyra Creator AI" className="w-8 h-8 object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.1)]" />
                <h3 className="font-serif text-2xl text-white mb-0">Replyra Creator</h3>
              </div>
              <p className="text-neutral-400 font-light text-sm leading-relaxed">
                Our proprietary visual forge. An advanced SaaS application generating high-end, 100% consistent virtual photography for your brand in seconds.
              </p>
            </div>
            <a href="https://replyra-creator.me" target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-brand hover:text-white transition-colors w-max">
              Enter Forge <ChevronRight size={12} />
            </a>
          </motion.div>

          {/* Box 4: Internal Technologies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.3 }}
            className="md:col-span-2 lg:col-span-1 p-8 md:p-12 border border-white/5 bg-white/[0.01] hover:bg-white/[0.02] backdrop-blur-sm transition-colors"
          >
            <h3 className="font-serif text-2xl mb-4 text-white">SaaS Engineering Forge</h3>
            <p className="text-neutral-400 font-light text-sm leading-relaxed">
              The global backbone of our digital operations. We build highly secure, custom AI and software solutions that solve complex automation challenges.
            </p>
          </motion.div>

          {/* Box 5: B2B Strategy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.4 }}
            className="md:col-span-2 lg:col-span-3 p-8 md:p-12 border border-white/5 bg-black relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-[url('/stardust.png')] opacity-10 mix-blend-overlay grayscale group-hover:opacity-20 transition-opacity" />
            <div className="relative z-10 lg:flex lg:items-center lg:justify-between lg:gap-12">
              <div className="lg:max-w-2xl">
                <h3 className="font-serif text-3xl mb-4 text-white">B2B & Venture Strategy</h3>
                <p className="text-neutral-400 font-light text-sm md:text-base leading-relaxed">
                  We don't just build these systems; we deploy them. From empowering the local founder with automated engagement to architecting highly intelligent funnels for startups and elite brands. We build the architecture that gives you your time back.
                </p>
              </div>
              <a href="#contact" onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }} className="mt-8 lg:mt-0 flex-shrink-0 inline-block px-6 py-3 border border-white/10 text-[10px] uppercase tracking-[0.2em] text-white hover:bg-white hover:text-black transition-all">
                Initiate Partnership Protocol
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
